import React from "react";
import {observer} from "mobx-react-lite";
import {EventStartAndEndDetails} from "../../../stores/domainStore/pages/models/eventStartAndEndDetails";
import "./eventRecurrenceEdit.sass";
import {
  recurrenceDaily,
  recurrenceWeekly,
  recurrenceMonthly,
  recurrenceEndsNever,
  recurrenceEndsOnThisDay
} from "../../../shared/constants";
import Selection from "../selection/selection";
import {addDays, endOfDay, format} from "date-fns";
import {EventRecurrenceDetails} from "../../../stores/domainStore/pages/models/eventRecurrenceDetails";
import {useTranslation} from "react-i18next";
import {RadioGroupKeyValue} from "../../../stores/observables/radioGroupKeyValues/radioGroupKeyValue";
import RadioGroupEdit from "../../molecules/radioGroupEdit/radioGroupEdit";
import {SimpleRadioGroupKeyValue} from "../../../stores/observables/radioGroupKeyValues/simpleRadioGroupKeyValue";
import {ObservableList} from "../../../stores/observables/observableList";
import {DatePickerRadioGroupKeyValue} from "../../../stores/observables/radioGroupKeyValues/datePickerRadioGroupKeyValue";
import {reaction, runInAction} from "mobx";
import parse from "html-react-parser";
import {utcToZonedTime} from "date-fns-tz";
import {min} from "lodash";
import EditField from "../../molecules/editField/editField";
import EventWeekEdit from "../eventWeekEdit/eventWeekEdit";
import {Col, Row} from "reactstrap";
import Banner from "../../atoms/banner/banner";
import {IconSource} from "../../../shared/enums/iconSource";
import EventDayEdit from "../eventDayEdit/eventDayEdit";

export interface IEventRecurrenceEditProps {
  eventRecurrenceDetails: EventRecurrenceDetails;
  eventStartAndEndDate: EventStartAndEndDetails;
  isEnhanced: boolean;
  isReleased?: boolean;
  isEditing?: boolean;
  nextReleaseStartDate?: Date;
}

const EventRecurrenceEdit: React.FC<IEventRecurrenceEditProps> = ({
  eventRecurrenceDetails,
  eventStartAndEndDate,
  isEnhanced,
  isReleased,
  isEditing,
  nextReleaseStartDate
}) => {
  // TODO: this is useful until CMS changes pseudo never-end date (1/1/9999) to null
  function isNeverEndingDate(date: Date | null) {
    const neverEndingEventDate = "9999-01-01";
    // CMS automatically sets recurrenceEndDate to "9999-01-01" when "Never" option is selected.
    // Dirty hack until backend is fixed - backend returns "9998-12-26" when event is never ending.
    return date == null || date > addDays(new Date(neverEndingEventDate), -30);
  }

  const eventEndsOnGivenDay = !isNeverEndingDate(
    eventRecurrenceDetails.recurrenceEndDate.date
  );

  const {t} = useTranslation(["translation"]);
  const reoccursNeverLabel = t("event:labels.reoccursNever");
  const reoccursOnThisDayLabel = t("event:labels.reoccursOnThisDay");

  const initialRecurrenceEndDate = eventRecurrenceDetails.recurrenceEndDate
    .initialValue
    ? new Date(eventRecurrenceDetails.recurrenceEndDate.initialValue)
    : null;

  const datePickerMinDate =
    isEditing && isReleased
      ? // allows users to choose the last day of the released month to end the series on.
        min([addDays(nextReleaseStartDate!, -1), initialRecurrenceEndDate])
      : eventStartAndEndDate.endDate.date;

  const items: RadioGroupKeyValue[] = [
    new SimpleRadioGroupKeyValue(recurrenceEndsNever, reoccursNeverLabel),
    new DatePickerRadioGroupKeyValue(
      recurrenceEndsOnThisDay,
      reoccursOnThisDayLabel,
      eventRecurrenceDetails.recurrenceEndDate,
      datePickerMinDate ?? new Date(),
      eventStartAndEndDate.timeZone,
      eventEndsOnGivenDay // show or hide recurrence end date picker.
    )
  ];

  reaction(
    () => eventStartAndEndDate.endDate.date,
    () =>
      runInAction(() => {
        eventRecurrenceDetails.recurrenceEndDate.setIsDirty(true);
      })
  );

  runInAction(() => {
    eventRecurrenceDetails.selectedRecurrenceEndType = eventEndsOnGivenDay
      ? recurrenceEndsOnThisDay
      : recurrenceEndsNever;
  });

  const recurrenceRadioGroupItems = new ObservableList<RadioGroupKeyValue>(
    items,
    eventEndsOnGivenDay ? recurrenceEndsOnThisDay : recurrenceEndsNever
  );

  function toggleDatePicker(item: RadioGroupKeyValue) {
    const enableDatePicker = item.value === recurrenceEndsOnThisDay;

    const datePicker = items.find(
      (value) => value.value === recurrenceEndsOnThisDay
    ) as DatePickerRadioGroupKeyValue;

    if (datePicker.enableDatePicker) {
      runInAction(() => {
        datePicker.enableDatePicker.value = enableDatePicker;
        eventRecurrenceDetails.datePickerSelected = enableDatePicker;

        // Clear recurrenceEndDate when event never ends recurring.
        if (!enableDatePicker) {
          datePicker.setDate(null);
        }

        //If on this day and recurrence end date is NED, set to current da
        if (enableDatePicker && !eventEndsOnGivenDay) {
          datePicker.setDate(datePickerMinDate ?? null);
        }
        if (enableDatePicker && datePickerMinDate)
          datePicker.setDate(endOfDay(datePickerMinDate));
      });
    }
  }

  const getFormattedHour = (date: Date | number) =>
    format(date, "h:mmaaa").replace(":00", "");

  const recurrenceSummaryMessage = (
    days: string,
    week: string,
    startTime: string,
    endTime: string,
    startDate: string
  ): string => {
    const messagePrefix = isEnhanced ? "enhanced" : "engage";
    const messageMiddle =
      eventRecurrenceDetails.frequency === recurrenceMonthly
        ? "RecurrenceSummaryMonthly"
        : "RecurrenceSummaryDailyWeekly";

    const message = `event:messages.${messagePrefix}${messageMiddle}`;

    const params = {
      days: days,
      weeks: week,
      startTime: startTime,
      startDate: startDate
    } as any;

    if (isEnhanced) {
      params.endTime = endTime;
    }

    return t(message, params);
  };

  function getRecurrenceSummary() {
    const startTime = getFormattedHour(
      utcToZonedTime(
        eventStartAndEndDate.startDate.date!,
        eventStartAndEndDate.timeZone
      )
    );
    const endTime = getFormattedHour(
      utcToZonedTime(
        eventStartAndEndDate.endDate.date!,
        eventStartAndEndDate.timeZone!
      )
    );
    const startDate = format(
      utcToZonedTime(
        eventStartAndEndDate.startDate.date!,
        eventStartAndEndDate.timeZone!
      ),
      "dd/MM/yyyy"
    );
    const week = eventRecurrenceDetails.monthWeeks.getDisplayLabel();
    const days =
      eventRecurrenceDetails.frequency === recurrenceDaily
        ? eventRecurrenceDetails.weekdays.getDisplayLabel()
        : format(
            utcToZonedTime(
              eventStartAndEndDate.startDate.date!,
              eventStartAndEndDate.timeZone!
            ),
            "iiii"
          );
    return parse(
      recurrenceSummaryMessage(days, week, startTime, endTime, startDate)
    );
  }

  return (
    <>
      <div className={"recurrence-edit"} data-testid="recurrenceSelection">
        <Row>
          <Col>
            <Selection
              value={eventRecurrenceDetails.getRecurrenceTypes()}
              label={t("event:labels.recurrenceOccurs")}
              isLoading={false}
              isDisabled={isEditing}
            />
          </Col>

          <Col>
            {eventRecurrenceDetails.frequency === recurrenceMonthly &&
              !isEditing && (
                <EventWeekEdit
                  eventStartDate={eventStartAndEndDate.startDate}
                  monthWeeks={eventRecurrenceDetails.monthWeeks}
                  timezone={eventStartAndEndDate.timeZone}
                  isEditing={!!isEditing}
                />
              )}
            {eventRecurrenceDetails.frequency === recurrenceDaily &&
              !isEditing && (
                <EventDayEdit
                  eventStartDate={eventStartAndEndDate.startDate}
                  weekdays={eventRecurrenceDetails.weekdays}
                  timezone={eventStartAndEndDate.timeZone}
                  isEditing={!!isEditing}
                />
              )}
          </Col>
        </Row>

        {(eventRecurrenceDetails.frequency === recurrenceDaily ||
          eventRecurrenceDetails.frequency === recurrenceWeekly ||
          eventRecurrenceDetails.frequency === recurrenceMonthly) && (
          <div
            data-testid={"recurrence-end-date-selection"}
            className={"recurrence-end-date-selection"}
          >
            {eventStartAndEndDate.startDate.date && (
              <div
                data-testid="recurrence-message-section"
                className={"recurrence-message"}
              >
                {getRecurrenceSummary()}
              </div>
            )}
            {eventRecurrenceDetails.monthWeeks.hasChange() && isEditing && (
              <div className={"week-change-warning-message"}>
                <Banner
                  text={t("event:errors.monthlyEventWeekChangeWarningMessage")}
                  icon={IconSource.info}
                />
              </div>
            )}
            <div
              data-testid="recurrence-end-date-section"
              className={"recurrence-date-picker "}
            >
              <EditField
                validationMessage={t(
                  "event:errors.recurrenceEndDateWarningMessage"
                )}
                showValidationMessage={
                  !eventRecurrenceDetails.recurrenceEndDate.isValid
                }
              >
                <RadioGroupEdit
                  items={recurrenceRadioGroupItems}
                  label={t("event:labels.recurrenceEnds")}
                  name="ends"
                  onSelect={(item: RadioGroupKeyValue) => {
                    runInAction(() => {
                      eventRecurrenceDetails.selectedRecurrenceEndType =
                        item.value as "endsNever" | "endsOnThisDay";
                      toggleDatePicker(item);
                    });
                  }}
                  isDisabled={false}
                />
              </EditField>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
export default observer(EventRecurrenceEdit);
